import { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import { Field, FieldInputProps } from 'react-final-form';

import { OptionProps } from './types';
import { Label, LabelText } from '../Typography';
import { ThemeProps } from '../../lib/theme';

const AlvarTooltip = dynamic(() => import('../Tooltip'));
const SelectComponent = dynamic(() => import('./Select'));
const CustomRange = dynamic(() => import('./CustomRange'));
const CustomSelect = dynamic(() => import('./CustomSelect'));

export interface AlvarFieldProps {
  className?: string;
  type?:
    | 'text'
    | 'password'
    | 'email'
    | 'textarea'
    | 'select'
    | 'checkbox'
    | 'radio'
    | 'custom-select'
    | 'button'
    | 'number'
    | 'tel'
    | 'custom-range';
  id?: string;
  name: string;
  label?: string;
  required?: boolean;
  readOnly?: boolean;
  options?: OptionProps;
  tooltip?: ReactNode;
  disabled?: boolean;
  fieldProps?: Record<string, unknown>;
  placeholder?: string;
  format?: (
    val: any,
  ) => {} /* NOTE: using {} here is a hackish solution, but the only thing that works as should. It's a workaround for Exclude<any, undefined>, so any but not undefined */;
  parse?: (value: any, name: string) => {};
  validate?: (val: any) => string;
  renderContent?: (
    content: ReactNode,
    inputProps: FieldInputProps<{}>,
  ) => ReactNode;
  formatOnBlur?: boolean;
  validateOnBlur?: boolean;
  min?: number;
  max?: number;
  step?: number;
  subscription?: Record<string, boolean>;
}

const SuggestedAction = styled.span``;

export const WarningMsg = styled.p<ThemeProps>(
  ({ theme: { fontSizes } }) => `
  position: absolute;
  top: 100%;
  font-size: calc(${fontSizes.small} - 2px);
  font-weight: 400;
  margin: .25em 0;
  max-width: 100%;
  overflow-wrap: break-word;

  ${SuggestedAction} {
    text-decoration: underline;

    &:hover {
      cursor: pointer;
    }
  }
`,
);

export const ErrorMsg = styled(WarningMsg)(
  ({ theme: { colors } }) => `
  color: ${colors.status.error};
`,
);

/* NOTE: weird selectors because as a selector select:read-only === select :mindblown: */
export const fieldStyle = css`
  ${({ theme: { colors, transitionTime } }) => `
    padding: .5rem 0;
    position: relative;
    background: none;
    border-bottom: 2px solid ${colors.borderColor};
    line-height: 1.2;
    font-weight: 500;
    transition-duration: ${transitionTime.normal};

    &:focus {
      outline: 0;
    }

    [aria-readonly='true'],
    &:read-only:not([aria-readonly='false']) {
      border-bottom-color: ${colors.borderColorReadOnly};
      font-weight: 400;
      color: ${colors.disabledColor};
    }

    &:not(:read-only),
    &[aria-readonly='false'] {
      border-bottom-width: 2px;
    }
  `}
`;

const StyledField = styled.span.withConfig({
  shouldForwardProp: (p) => !['pristine'].includes(p),
})`
  ${fieldStyle};
`;

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

export const Input = styled(StyledField.withComponent('input')).withConfig({
  shouldForwardProp: (p) => !['loading'].includes(p),
})`
  &:read-only {
    cursor: default;
  }

  &[data-loading='true'] {
    pointer-events: none;
    opacity: 0.8;
  }
`;

const Textarea = styled(StyledField.withComponent('textarea'))`
  min-height: 120px;
  padding: 0.5rem 0.5rem;
  background: ${(p) => p.theme.colors.mellowHighlightColor};
`;

const Select = StyledField.withComponent(SelectComponent);

const fieldComponents = {
  input: Input,
  textarea: Textarea,
  select: Select,
  'custom-select': CustomSelect,
  'custom-range': CustomRange,
};

const getFieldComponent = (type: string): any =>
  fieldComponents[type as keyof typeof fieldComponents] || Input;

const FormField = ({
  className,
  type = 'text',
  name,
  label,
  required,
  readOnly,
  options,
  id,
  fieldProps,
  disabled,
  placeholder,
  renderContent,
  tooltip,
  min,
  max,
  step,
  subscription,
  ...rest
}: AlvarFieldProps) => {
  const FieldComponent = getFieldComponent(type);

  return (
    <Field {...{ name, type, required, subscription, ...rest }}>
      {({ input, meta }) => {
        const {
          active,
          touched,
          submitError,
          submitFailed,
          error,
          pristine,
          data,
          modified,
        } = meta;
        const showError =
          ((active && modified) || touched || submitFailed) &&
          (error || submitError);
        const showWarning = data?.warning && !showError;

        const content = (
          <>
            <FieldComponent
              id={id}
              {...input}
              {...{
                readOnly,
                disabled,
                options,
                placeholder,
                pristine,
                min,
                max,
                step,
              }}
              {...fieldProps}
              className={label ? undefined : className}
            />
            {showError && <ErrorMsg>{meta.error || meta.submitError}</ErrorMsg>}
            {showWarning && (
              <WarningMsg>
                {data?.warning}{' '}
                {data?.suggestion && (
                  <SuggestedAction
                    onClick={() => input.onChange(data.suggestion.value)}
                  >
                    {data.suggestion.text}
                  </SuggestedAction>
                )}
              </WarningMsg>
            )}
          </>
        );

        return label ? (
          <>
            <Label
              className={className}
              title={label}
              hasError={!!showError}
              data-error={!!showError}
            >
              <LabelText isRequired={required} isReadOnly={readOnly}>
                {label || capitalize(name)}
                {tooltip ? <AlvarTooltip content={tooltip} /> : null}
              </LabelText>
              {content}
            </Label>
          </>
        ) : (
          <>{renderContent ? renderContent(content, input) : content}</>
        );
      }}
    </Field>
  );
};

export default FormField;
